import { useState, useEffect, useRef, useCallback } from 'react';

export interface SessionTimerState {
  isRunning: boolean;
  elapsedSeconds: number;
  elapsedMinutes: number;
  formattedTime: string;
}

export interface SessionTimerActions {
  start: () => void;
  pause: () => void;
  stop: () => void;
  reset: () => void;
  getMinutesUsed: () => number;
}

export const useSessionTimer = (): [SessionTimerState, SessionTimerActions] => {
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number | null>(null);

  const start = useCallback(() => {
    if (!isRunning) {
      startTimeRef.current = Date.now() - (elapsedSeconds * 1000);
      setIsRunning(true);
    }
  }, [isRunning, elapsedSeconds]);

  const pause = useCallback(() => {
    setIsRunning(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const stop = useCallback(() => {
    setIsRunning(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    setElapsedSeconds(0);
    setIsRunning(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    startTimeRef.current = null;
  }, []);

  const getMinutesUsed = useCallback((): number => {
    // Always round down - if 22:50, return 22 minutes
    return Math.floor(elapsedSeconds / 60);
  }, [elapsedSeconds]);

  useEffect(() => {
    if (isRunning) {
      intervalRef.current = setInterval(() => {
        if (startTimeRef.current) {
          const now = Date.now();
          const elapsed = Math.floor((now - startTimeRef.current) / 1000);
          setElapsedSeconds(elapsed);
        }
      }, 1000);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isRunning]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const elapsedMinutes = Math.floor(elapsedSeconds / 60);
  const displaySeconds = elapsedSeconds % 60;
  const formattedTime = `${elapsedMinutes.toString().padStart(2, '0')}:${displaySeconds.toString().padStart(2, '0')}`;

  const state: SessionTimerState = {
    isRunning,
    elapsedSeconds,
    elapsedMinutes,
    formattedTime,
  };

  const actions: SessionTimerActions = {
    start,
    pause,
    stop,
    reset,
    getMinutesUsed,
  };

  return [state, actions];
};